<i18n>
  {
    "fr": {
      "street": "Rue",
      "city": "Ville",
      "zip-code": "Code postal",
      "country": "Pays",
      "errors": {
        "max_length": "50 Caractères maximum",
        "required": "Champ requis"
      }
    },
    "en": {
      "street": "Street",
      "city": "City",
      "zip-code": "Zip code",
      "country": "Country",
      "errors": {
        "max_length": "50 characters max.",
        "required": "Required field"
      }
    }
  }
</i18n>

<template>
  <v-row no-gutters>
    <v-col
      cols="6"
      class="pr-2"
    >
      <v-text-field
        v-model="address"
        :label="$t('street')"
        :error-messages="addressErrors"
        height="42px"
        dense
        outlined
        @input="addressTouch"
        @blur="addressTouch"
      >
        <template
          #label
        >
          {{ $t('street' ) }}
          <span
            v-if="!address"
            class="red--text"
          >
            <strong> *</strong>
          </span>
        </template>
      </v-text-field>
    </v-col>

    <v-col
      cols="2"
      class="px-2"
    >
      <v-text-field
        v-model="city"
        :label="$t('city')"
        :error-messages="cityErrors"
        height="42px"
        dense
        outlined
        @input="cityTouch"
        @blur="cityTouch"
      >
        <template
          #label
        >
          {{ $t('city' ) }}
          <span
            v-if="!city"
            class="red--text"
          >
            <strong> *</strong>
          </span>
        </template>
      </v-text-field>
    </v-col>

    <v-col
      cols="2"
      class="px-2"
    >
      <v-text-field
        v-model="zipCode"
        :label="$t('zip-code')"
        :error-messages="zipCodeErrors"
        height="42px"
        dense
        outlined
        @input="zipCodeTouch"
        @blur="zipCodeTouch"
      >
        <template
          #label
        >
          {{ $t('zip-code' ) }}
          <span
            v-if="!zipCode"
            class="red--text"
          >
            <strong> *</strong>
          </span>
        </template>
      </v-text-field>
    </v-col>

    <v-col
      cols="2"
      class="pl-2"
    >
      <v-select
        v-model="country"
        :items="countries"
        item-text="native_name"
        item-value="alpha_2_code"
        return-object
        :menu-props="{'offset-y': true, 'auto': true}"
        :label="$t('country')"
        :error-messages="countryErrors"
        height="42px"
        dense
        outlined
        @input="countryTouch"
        @blur="countryTouch"
      >
        <template #item="{ item }">
          <div class="v-list-item__title">
            {{ item.alpha_2_code }} - {{ item.native_name }}
          </div>
        </template>
        <template
          #label
        >
          {{ $t('country' ) }}
          <span
            v-if="!country"
            class="red--text"
          >
            <strong> *</strong>
          </span>
        </template>
      </v-select>
    </v-col>
  </v-row>
</template>

<script>
import _ from 'lodash'
import { mapMutations, mapState } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required, maxLength } from 'vuelidate/lib/validators'

export default {
  name: 'FlightCustomerContactAddress',
  mixins: [validationMixin],
  computed: {
    ...mapState({
      countries: state => state.general.countries,
      contactAddress: state => state.fb.passengersInformation.contactAddress,
      contactCity: state => state.fb.passengersInformation.contactCity,
      contactZipCode: state => state.fb.passengersInformation.contactZipCode,
      contactCountry: state => state.fb.passengersInformation.contactCountry
    }),
    address: {
      /**
       * Just return vueX store state
       * @return {string}
       */
      get () {
        return this.contactAddress
      },
      /**
       * Change the vuelidate state of this field, then call to a method who change vueX state
       * @param newVal {string}
       * @return {undefined}
       */
      set (newVal) {
        this.addressTouch()
        this.changeContactAddressDebounce(newVal)
      }
    },
    /**
     * Return an array of errors based on vuelidate state of address field
     * @return {string[]}
     */
    addressErrors () {
      const errors = []
      if (!this.$v.address.$dirty) return errors
      !this.$v.address.maxLength && errors.push(this.$i18n.t('errors.max-length'))
      !this.$v.address.required && errors.push(this.$i18n.t('errors.required'))
      return errors
    },
    city: {
      /**
       * Return vueX store state
       * @return {string}
       */
      get () {
        return this.contactCity
      },
      /**
       * Change the vuelidate state of this field, then call to a method who change vueX state
       * @param newVal {string}
       * @return {undefined}
       */
      set (newVal) {
        this.cityTouch()
        this.changeContactCityDebounce(newVal)
      }
    },
    /**
     * Return an array of errors based on vuelidate state of address field
     * @return {string[]}
     */
    cityErrors () {
      const errors = []
      if (!this.$v.city.$dirty) return errors
      !this.$v.city.required && errors.push(this.$i18n.t('errors.required'))
      return errors
    },
    zipCode: {
      /**
       * Return vueX store state
       * @return {string}
       */
      get () {
        return this.contactZipCode
      },
      /**
       * Change the vuelidate state of this field, then call to a method who change vueX state
       * @param newVal {string}
       * @return {undefined}
       */
      set (newVal) {
        this.zipCodeTouch()
        this.changeContactZipCodeDebounce(newVal)
      }
    },
    /**
     * Return an array of errors based on vuelidate state of address field
     * @return {string[]}
     */
    zipCodeErrors () {
      const errors = []
      if (!this.$v.zipCode.$dirty) return errors
      !this.$v.zipCode.required && errors.push(this.$i18n.t('errors.required'))
      return errors
    },
    country: {
      /**
       * Return vueX store state
       * @return {string}
       */
      get () {
        return this.contactCountry
      },
      /**
       * Change the vuelidate state of this field, then call to a method who change vueX state
       * @param newVal {string}
       * @return {undefined}
       */
      set (newVal) {
        this.countryTouch()
        this.changeContactCountryDebounce(newVal)
      }
    },
    /**
     * Return an array of errors based on vuelidate state of address field
     * @return {string[]}
     */
    countryErrors () {
      const errors = []
      if (!this.$v.country.$dirty) return errors
      !this.$v.country.required && errors.push(this.$i18n.t('errors.required'))
      return errors
    },
    /**
     * Return the opposite of vuelidate invalid parameters (to improve readability of code)
     * This value need to be computed for the watcher to work properly
     * @return {boolean}
     */
    validity () {
      return !this.$v.$invalid
    }
  },
  watch: {
    /**
     * Watch for value change of validity then commit the new value to the store
     * @param newVal {boolean}
     */
    validity (newVal) {
      this.changeContactAddressValidity(newVal)
    }
  },
  validations: {
    address: {
      required,
      maxLength: maxLength(50)
    },
    city: {
      required
    },
    zipCode: {
      required
    },
    country: {
      required
    }
  },
  created () {
    this.initAddress()
  },
  methods: {
    ...mapMutations({
      changeContactAddressValidity: 'fb/passengersInformation/changeContactAddressValidity',
      changeContactAddress: 'fb/passengersInformation/changeContactAddress',
      changeContactCity: 'fb/passengersInformation/changeContactCity',
      changeContactZipCode: 'fb/passengersInformation/changeContactZipCode',
      changeContactCountry: 'fb/passengersInformation/changeContactCountry'
    }),
    /**
     * Retrieve customer object from vueX store in the first place,
     * then try to assign value to data if the customer got them
     * @return {undefined}
     */
    initAddress () {
      const customer = this.$store.getters['general/getCustomer']
      if (customer.street_1) {
        this.address = customer.street_1
      }
      if (customer.city) {
        this.city = customer.city
      }
      if (customer.zip_code) {
        this.zipCode = customer.zip_code
      }
      if (customer.country_name && customer.country_code) {
        this.country = {
          alpha_2_code: customer.country_code
        }
      }
    },
    /**
     * A simple debounced vuelidate $touch method
     * @return {undefined}
     */
    addressTouch: _.debounce(function () {
      this.$v.address.$touch()
    }, 600),
    /**
     * A simple debounced vuelidate $touch method
     * @return {undefined}
     */
    cityTouch: _.debounce(function () {
      this.$v.city.$touch()
    }, 600),
    /**
     * A simple debounced vuelidate $touch method
     * @return {undefined}
     */
    zipCodeTouch: _.debounce(function () {
      this.$v.zipCode.$touch()
    }, 600),
    /**
     * A simple debounced vuelidate $touch method
     * @return {undefined}
     */
    countryTouch: _.debounce(function () {
      this.$v.country.$touch()
    }, 600),
    /**
     * A simple debounced vuelidate $touch method
     * @return {undefined}
     */
    changeContactAddressDebounce: _.debounce(function (newVal) {
      this.changeContactAddress(newVal)
    }, 600),
    /**
     * A simple debounced vuelidate $touch method
     * @return {undefined}
     */
    changeContactCityDebounce: _.debounce(function (newVal) {
      this.changeContactCity(newVal)
    }, 600),
    /**
     * A simple debounced vuelidate $touch method
     * @return {undefined}
     */
    changeContactZipCodeDebounce: _.debounce(function (newVal) {
      this.changeContactZipCode(newVal)
    }, 600),
    /**
     * A simple debounced vuelidate $touch method
     * @return {undefined}
     */
    changeContactCountryDebounce: _.debounce(function (newVal) {
      this.changeContactCountry(newVal)
    }, 600)
  }
}
</script>

<style scoped>

</style>
